import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AccessTokenResponse } from '../login/objects/accessTokenResponse';
import { ClaimsResponse } from '../login/objects/claimsResponse';

@Injectable({
	providedIn: 'root'
})
export class SessionService {
	private readonly ACCESS_TOKEN_KEY = 'luna_access_token';
	private readonly REFRESH_TOKEN_KEY = 'luna_refresh_token';
	private readonly TOKEN_EXPIRY_KEY = 'luna_token_expiry';
	private readonly USER_INFO_KEY = 'luna_user_info';

	private sessionValidSubject = new BehaviorSubject<boolean>(false);
	public sessionValid$ = this.sessionValidSubject.asObservable();

	constructor() {
		this.checkSessionValidity();
	}

	storeTokens(tokenResponse: AccessTokenResponse): void {
		localStorage.setItem(this.ACCESS_TOKEN_KEY, tokenResponse.accessToken);
		localStorage.setItem(this.REFRESH_TOKEN_KEY, tokenResponse.refreshToken);
		localStorage.setItem(this.TOKEN_EXPIRY_KEY, (Date.now() + (tokenResponse.expiresIn * 1000)).toString());
		this.sessionValidSubject.next(true);
	}

	storeUserInfo(userInfo: ClaimsResponse): void {
		localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
	}

	getAccessToken(): string | null {
		return localStorage.getItem(this.ACCESS_TOKEN_KEY);
	}

	getRefreshToken(): string | null {
		return localStorage.getItem(this.REFRESH_TOKEN_KEY);
	}

	getUserInfo(): ClaimsResponse | null {
		const stored = localStorage.getItem(this.USER_INFO_KEY);
		return stored ? JSON.parse(stored) : null;
	}

	isTokenExpired(): boolean {
		const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
		if (!expiry) return true;
		return Date.now() > parseInt(expiry);
	}

	clearSession(): void {
		localStorage.clear();
		this.sessionValidSubject.next(false);
		sessionStorage.clear();
	}

	private checkSessionValidity(): void {
		const hasToken = !!this.getAccessToken();
		const notExpired = !this.isTokenExpired();
		this.sessionValidSubject.next(hasToken && notExpired);
	}

	getClaim(claimName: string): string | null {
		const userInfo = this.getUserInfo();
		if (!userInfo?.claims) return null;

		const claim = userInfo.claims.find(c => c.name === claimName);
		return claim?.value || null;
	}

	isInternalUser(): boolean {
		const userInfo = this.getUserInfo();
		if (!userInfo?.claims) return false;

		const partnerId = this.getClaim('partnerId');
		const manufacturerId = this.getClaim('manufacturerId');

		return !partnerId && !manufacturerId;
	}

	isPartnerUser(): boolean {
		const partnerId = this.getClaim('partnerId');
		return !!partnerId && partnerId !== '';
	}

	isManufacturerUser(): boolean {
		const manufacturerId = this.getClaim('manufacturerId');
		const manufacturerRep = this.getClaim('manufacturerRep');
		const manufacturerRepAdmin = this.getClaim('manufacturerRepAdmin');

		return !!manufacturerId && manufacturerId !== '' &&
			(manufacturerRep === 'True' || manufacturerRepAdmin === 'True');
	}

	getPartnerId(): string | null {
		return this.getClaim('partnerId');
	}

	getManufacturerId(): string | null {
		return this.getClaim('manufacturerId');
	}
}
